import { useEffect, useState, type FormEvent } from 'react';
import SettingsBreadcrumb from '@/pages/settings/components/SettingsBreadcrumb';
import { supabase } from '@/lib/supabase';

interface SocialForm {
  name: string;
  whatsapp: string;
  instagram_url: string;
  facebook_url: string;
  tiktok_url: string;
}

const emptyForm: SocialForm = {
  name: '',
  whatsapp: '',
  instagram_url: '',
  facebook_url: '',
  tiktok_url: '',
};

export default function SocialSettingsPage() {
  const [id, setId] = useState<string | null>(null);
  const [form, setForm] = useState<SocialForm>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: e } = await supabase
          .from('business_info')
          .select('id, name, whatsapp, instagram_url, facebook_url, tiktok_url')
          .limit(1)
          .maybeSingle();
        if (e) throw e;
        if (!active) return;
        if (data) {
          setId(data.id as string);
          setForm({
            name: (data.name as string) ?? '',
            whatsapp: (data.whatsapp as string) ?? '',
            instagram_url: (data.instagram_url as string) ?? '',
            facebook_url: (data.facebook_url as string) ?? '',
            tiktok_url: (data.tiktok_url as string) ?? '',
          });
        }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'No se pudo cargar la información');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const set = (key: keyof SocialForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSuccess(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const { error: e2 } = await supabase
        .from('business_info')
        .update({
          name: form.name.trim() || null,
          whatsapp: form.whatsapp.trim() || null,
          instagram_url: form.instagram_url.trim() || null,
          facebook_url: form.facebook_url.trim() || null,
          tiktok_url: form.tiktok_url.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (e2) throw e2;
      setSuccess('Redes sociales y contacto guardados correctamente.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  return (
    <div className="max-w-3xl">
      <SettingsBreadcrumb label="Redes sociales y contacto" icon="ri-share-circle-line" />
      <h1 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
        Redes sociales y contacto
      </h1>
      <p className="mt-2 text-foreground-600">
        Estos datos son generales del negocio. Las redes sociales por sucursal se configuran en la
        sección <span className="font-medium text-foreground-800">Sucursales</span> y se muestran
        encima del nombre de cada sucursal en el catálogo.
      </p>

      {loading ? (
        <div className="mt-8 flex items-center justify-center py-16">
          <div className="w-9 h-9 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : !id ? (
        <div className="mt-6 rounded-lg border border-dashed border-background-300 p-10 text-center">
          <i className="ri-error-warning-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">
            No se encontró la información del negocio para editar.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {error && (
            <div className="rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 flex items-center gap-2 text-sm text-foreground-700">
              <i className="ri-checkbox-circle-line text-accent-700" /> {success}
            </div>
          )}

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 space-y-4">
            <h2 className="font-heading font-semibold text-foreground-950 flex items-center gap-2">
              <i className="ri-contacts-book-2-line text-primary-700" /> Contacto
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Nombre del negocio</label>
                <input className={inputClass} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Ej. DK Supply" />
              </div>
              <div>
                <label className={labelClass}>WhatsApp general</label>
                <input className={inputClass} value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} placeholder="18095551234" />
                <p className="mt-1 text-xs text-foreground-400">Con código de país, sin espacios ni guiones.</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 space-y-4">
            <h2 className="font-heading font-semibold text-foreground-950 flex items-center gap-2">
              <i className="ri-share-line text-primary-700" /> Redes sociales
            </h2>
            <p className="text-sm text-foreground-500 -mt-2">
              Deja en blanco la que no quieras mostrar en el catálogo.
            </p>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>
                  <i className="ri-instagram-line mr-1 text-accent-600" /> Instagram
                </label>
                <input className={inputClass} value={form.instagram_url} onChange={(e) => set('instagram_url', e.target.value)} placeholder="https://instagram.com/tu_usuario o @tu_usuario" />
              </div>
              <div>
                <label className={labelClass}>
                  <i className="ri-facebook-circle-line mr-1 text-primary-700" /> Facebook
                </label>
                <input className={inputClass} value={form.facebook_url} onChange={(e) => set('facebook_url', e.target.value)} placeholder="https://facebook.com/tu_pagina" />
              </div>
              <div>
                <label className={labelClass}>
                  <i className="ri-tiktok-line mr-1" /> TikTok
                </label>
                <input className={inputClass} value={form.tiktok_url} onChange={(e) => set('tiktok_url', e.target.value)} placeholder="https://tiktok.com/@tu_usuario o @tu_usuario" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
            >
              <i className="ri-save-line" />
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}